/**
 * Client for the local forced-alignment helper (tools/aligner-server.py).
 *
 * The helper wraps Meta's MMS CTC forced aligner (1,100+ languages): given the
 * audio and the words in spoken order, it MEASURES where each word sits by
 * aligning phonemes to audio frames — the thing generative models cannot do.
 * When the helper is not running, the app falls back to the legacy
 * Gemini-timestamp pipeline; `isAlignerAvailable` is the switch.
 */

/*
 * Re-exported, not re-declared: the port has to match `tools/aligner-server.py`
 * and `src/config` is where that agreement is recorded. The export name is
 * unchanged, so no call site here or elsewhere moves.
 */
export { ALIGNER_URL } from '../config';
import { ALIGNER_URL } from '../config';

export interface AlignedWord {
  /** Measured start, seconds. */
  start: number;
  /** Measured end, seconds. CTC ends are tight; extend to VAD edges downstream. */
  end: number;
  /** The transcript word this row aligns (same order as submitted). */
  text: string;
  /** Log-prob style score; nearer 0 = better. Very negative = suspect region. */
  score: number;
}

/** Quick health probe with a short timeout — used to pick the pipeline. */
export async function isAlignerAvailable(timeoutMs = 1500): Promise<boolean> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const resp = await fetch(`${ALIGNER_URL}/health`, { signal: ctrl.signal });
      if (!resp.ok) return false;
      const data = (await resp.json()) as { ok?: boolean };
      return data.ok === true;
    } finally {
      clearTimeout(timer);
    }
  } catch {
    return false;
  }
}

export interface AlignRequest {
  /** Mono WAV blob (any rate; 16 kHz preferred — matches the model). */
  wav: Blob;
  /** Space-separated words in spoken order (fillers included, noise excluded). */
  transcript: string;
  /** ISO 639-3 language for romanization, e.g. 'ara', 'eng'. */
  language: string;
  signal?: AbortSignal;
  /** Alignment of ~45s takes ~15s on CPU; default allows much longer files. */
  timeoutMs?: number;
}

export async function alignTranscript(req: AlignRequest): Promise<AlignedWord[]> {
  const b64 = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result).split(',')[1] ?? '');
    fr.onerror = () => reject(new Error('Could not read audio for alignment.'));
    fr.readAsDataURL(req.wav);
  });

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), req.timeoutMs ?? 10 * 60 * 1000);
  const onCallerAbort = () => ctrl.abort();
  req.signal?.addEventListener('abort', onCallerAbort, { once: true });
  try {
    const resp = await fetch(`${ALIGNER_URL}/align`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wav_b64: b64, transcript: req.transcript, language: req.language }),
      signal: ctrl.signal,
    });
    const data = (await resp.json()) as { words?: AlignedWord[]; error?: string };
    if (!resp.ok || !Array.isArray(data.words)) {
      throw new Error(data.error || `Aligner returned HTTP ${resp.status}.`);
    }
    return data.words.map((w) => ({
      start: Number(w.start) || 0,
      end: Number(w.end) || 0,
      text: String(w.text ?? ''),
      score: Number(w.score) || 0,
    }));
  } finally {
    clearTimeout(timer);
    req.signal?.removeEventListener('abort', onCallerAbort);
  }
}

/**
 * Map the aligner's summed log-prob score to the app's 0..1 confidence.
 * Near-zero scores are solid alignments; very negative ones mean the audio
 * under the word does not sound like the word — exactly what should surface
 * first in Verify.
 *
 * Lives here, with the scores it interprets: the WhisperX base deliberately
 * re-maps its own 0..1 confidences onto this same log-prob-ish scale so that
 * both engines grade identically, and that only makes sense if there is one
 * scale, defined next to the aligner that produces it.
 */
export function confidenceFromScore(score: number): number {
  if (score > -1) return 0.9;
  if (score > -4) return 0.75;
  if (score > -10) return 0.5;
  return 0.3;
}
